import { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { Button, Field, LoadingState, MessageState, TextArea, TextButton, useFeedback } from "../../../../../ds";
import { Comment } from "../../../models/Comment";
import { CommentDetailDTO } from "../../../models/services/GetPageComments";
import { CommentService } from "../../../services/CommentService";
import { CreateCommentRequest } from "../../../models/services/CreateCommentRequest";
import { UpdateCommentRequest } from "../../../models/services/UpdateCommentRequest";
import { useServiceCallPro } from "../../../../../common/services/UseServiceCall";

const commentSvc = new CommentService();

export const DeletedCommentText = "[Comment Deleted]";

interface ReplyComment {
    parent: Comment;
    child?: Comment;
    publishMode: "new" | "edit";
}

const mapCommentDetailDtoToComment = (commentDetail: CommentDetailDTO): Comment => ({
    commentId: commentDetail.commentId,
    content: commentDetail.content,
    children: commentDetail.comments?.map(mapCommentDetailDtoToComment) || [],
    profileName: commentDetail.authorName,
    timeStamp: DateTime.fromISO(commentDetail.timeStamp),
    isAuthor: commentDetail.isAuthor,
    isDeleted: commentDetail.isDeleted,
    isEdited: commentDetail.isEdited,
});

function initials(name: string): string {
    const names = (name ?? "").trim().split(/\s+/).filter(Boolean);
    if (names.length === 0) return "?";
    return (names[0].charAt(0) + (names.length > 1 ? names[names.length - 1].charAt(0) : "")).toUpperCase();
}

function countComments(comments: Comment[]): number {
    return comments.reduce((n, c) => n + 1 + c.children.length, 0);
}

interface CommentItemProps {
    item: Comment;
    isReply?: boolean;
    onReply: () => void;
    onEdit: () => void;
    onDelete: () => void;
    children?: React.ReactNode;
}

/** One comment: initials, name, date, text (or "[Comment Deleted]") and Reply · Edit · Delete (edit / delete: the author's). */
function CommentItem(props: CommentItemProps) {
    const { item } = props;
    const canChange = item.isAuthor && !item.isDeleted;
    return (
        <li className={props.isReply ? "ark-comment is-reply" : "ark-comment"}>
            <div className="ark-comment-head">
                <span className="ark-comment-avatar" aria-hidden="true">{initials(item.profileName)}</span>
                <span className="ark-comment-name">{item.profileName}</span>
                {item.timeStamp?.isValid ? (
                    <time className="ark-comment-date" dateTime={item.timeStamp.toISO() ?? undefined}>
                        {item.timeStamp.toFormat("dd LLL yyyy, HH:mm")}
                    </time>
                ) : null}
            </div>
            <p className={item.isDeleted ? "ark-comment-text is-deleted" : "ark-comment-text"}>
                {item.isDeleted ? DeletedCommentText : item.content}
                {item.isEdited && !item.isDeleted ? <span className="ark-comment-edited"> (edited)</span> : null}
            </p>
            <div className="ark-comment-actions">
                <TextButton type="primary" text="Reply" aria-label={`Reply to ${item.profileName}`} onClick={props.onReply} />
                {canChange ? (
                    <>
                        <TextButton type="primary" text="Edit" aria-label="Edit your comment" onClick={props.onEdit} />
                        <TextButton type="primary" text="Delete" aria-label="Delete your comment" onClick={props.onDelete} />
                    </>
                ) : null}
            </div>
            {props.children}
        </li>
    );
}

export interface CommentsProps {
    /** Default: the route's `spaceId` / `pageId`. */
    spaceId?: string;
    pageId?: string;
    /** Draws its own "Comments" heading (the full-width route); the side panel has the panel title instead. */
    showTitle?: boolean;
    /** The number of comments (replies included) after each load. */
    onCountChange?: (count: number) => void;
}

/**
 * The page's comments (side panel, phone card and the `…/comments` route): a "Write a comment" box with Publish, then the
 * threads, one level deep. Reply works on any comment (a reply to a reply joins the same thread); Edit and Delete are the
 * author's. A deleted comment reads "[Comment Deleted]".
 */
export function Comments(props: CommentsProps) {
    const params = useParams();
    const spaceId = props.spaceId ?? params.spaceId;
    const pageId = props.pageId ?? params.pageId;
    const feedback = useFeedback();
    const { onCountChange } = props;

    const [comments, setComments] = useState<Comment[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [publishCommentInfo, setPublishCommentInfo] = useState<ReplyComment>();
    const [commentText, setCommentText] = useState("");
    const [replyText, setReplyText] = useState("");

    const getPageCommentsCall = useServiceCallPro(commentSvc.getPageComments);
    const createCommentCall = useServiceCallPro(commentSvc.createComment);
    const editCommentCall = useServiceCallPro(commentSvc.editComment);
    const deleteCommentCall = useServiceCallPro(commentSvc.deleteComment);
    const isBusy = createCommentCall.isLoading || editCommentCall.isLoading;

    /****************************
     * DATA REQUESTS
     *****************************/

    const getComments = useCallback(() => {
        if (!spaceId || !pageId) return;
        getPageCommentsCall
            .invoke(spaceId, pageId)
            .then((response) => {
                const mapped = response.comments.map(mapCommentDetailDtoToComment);
                setComments(mapped);
                onCountChange?.(countComments(mapped));
            })
            .catch((error) => feedback.error(error, "Error!"))
            .finally(() => setLoaded(true));
    }, [spaceId, pageId, getPageCommentsCall.invoke, onCountChange, feedback]);

    const createComment = useCallback(
        (comment: string, parentCommentId: string | undefined) => {
            if (!spaceId || !pageId) return;
            const request: CreateCommentRequest = { content: comment, parentCommentId };
            createCommentCall
                .invoke(request, spaceId, pageId)
                .then(() => {
                    getComments();
                    setPublishCommentInfo(undefined);
                    setReplyText("");
                    if (!parentCommentId) setCommentText("");
                })
                .catch((error) => feedback.error(error, "Error!"));
        },
        [spaceId, pageId, createCommentCall.invoke, getComments, feedback]
    );

    const editComment = useCallback(
        (comment: string) => {
            const updatingCommentId = publishCommentInfo?.child?.commentId || publishCommentInfo?.parent.commentId || "";
            if (!spaceId || !pageId || !updatingCommentId) return;
            const request: UpdateCommentRequest = { content: comment };
            editCommentCall
                .invoke(request, spaceId, pageId, updatingCommentId)
                .then(() => {
                    getComments();
                    setPublishCommentInfo(undefined);
                    setReplyText("");
                })
                .catch((error) => feedback.error(error, "Error!"));
        },
        [getComments, spaceId, pageId, publishCommentInfo, editCommentCall.invoke, feedback]
    );

    const deleteComment = useCallback(
        (comment: Comment) => {
            const deleteCommentId = comment.commentId || "";
            if (!spaceId || !pageId || !deleteCommentId) return;
            deleteCommentCall
                .invoke(spaceId, pageId, deleteCommentId)
                .then(() => {
                    getComments();
                    setPublishCommentInfo(undefined);
                })
                .catch((error) => feedback.error(error, "Error!"));
        },
        [getComments, spaceId, pageId, deleteCommentCall.invoke, feedback]
    );

    /****************************
     * EFFECTS
     *****************************/

    useEffect(() => {
        setLoaded(false);
        setComments([]);
        setPublishCommentInfo(undefined);
        getComments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaceId, pageId]);

    const openInline = useCallback((info: ReplyComment) => {
        setPublishCommentInfo(info);
        setReplyText(info.publishMode === "edit" ? (info.child?.content ?? info.parent.content ?? "") : "");
    }, []);

    /****************************
     * USER ACTIONS
     *****************************/

    const handleReplyOrEditPublish = useCallback(() => {
        const text = replyText.trim();
        if (!spaceId || !pageId || !text || !publishCommentInfo) return;
        if (publishCommentInfo.publishMode === "edit") editComment(replyText);
        else createComment(replyText, publishCommentInfo.parent.commentId);
    }, [replyText, spaceId, pageId, publishCommentInfo, editComment, createComment]);

    const handleNewComment = useCallback(() => {
        if (!spaceId || !pageId || !commentText.trim()) return;
        createComment(commentText, undefined);
    }, [spaceId, pageId, commentText, createComment]);

    /****************************
     * HTML
     *****************************/

    const inlineEditor = (
        <div className="ark-comment-compose is-inline">
            <Field label={publishCommentInfo?.publishMode === "edit" ? "Edit comment" : "Reply"}>
                <TextArea
                    value={replyText}
                    onChange={setReplyText}
                    rows={3}
                    autoFocus
                    placeholder="Write a comment"
                    disabled={isBusy}
                />
            </Field>
            <div className="ark-comment-compose-actions">
                <Button type="white" size="sm" text="Cancel" onClick={() => setPublishCommentInfo(undefined)} />
                <Button
                    type="secondary"
                    size="sm"
                    text={publishCommentInfo?.publishMode === "edit" ? "Edit" : "Reply"}
                    isBusy={isBusy}
                    isDisabled={!replyText.trim()}
                    onClick={handleReplyOrEditPublish}
                />
            </div>
        </div>
    );

    const isInlineUnder = (comment: Comment, child?: Comment) =>
        !!publishCommentInfo &&
        (child
            ? publishCommentInfo.child?.commentId === child.commentId
            : !publishCommentInfo.child && publishCommentInfo.parent.commentId === comment.commentId);

    return (
        <section className="ark-comments" aria-label={props.showTitle ? undefined : "Comments"} aria-labelledby={props.showTitle ? "ark-comments-title" : undefined}>
            {props.showTitle ? <h2 id="ark-comments-title" className="ark-comments-title">Comments</h2> : null}

            {!loaded && getPageCommentsCall.isLoading ? <LoadingState variant="inline" text="Loading comments…" /> : null}

            {loaded && comments.length === 0 ? (
                <MessageState compact illustration={null} className="ark-comments-empty" title="No comments yet" text="Questions and notes about this page go here." />
            ) : null}

            {comments.length > 0 ? (
                <ol className="ark-comment-list">
                    {comments.map((comment) => (
                        <CommentItem
                            key={comment.commentId}
                            item={comment}
                            onReply={() => openInline({ parent: comment, publishMode: "new" })}
                            onEdit={() => openInline({ parent: comment, publishMode: "edit" })}
                            onDelete={() => deleteComment(comment)}
                        >
                            {isInlineUnder(comment) ? inlineEditor : null}
                            {comment.children.length > 0 ? (
                                <ol className="ark-comment-replies">
                                    {comment.children.map((subComment) => (
                                        <CommentItem
                                            key={subComment.commentId}
                                            item={subComment}
                                            isReply
                                            onReply={() => openInline({ parent: comment, child: subComment, publishMode: "new" })}
                                            onEdit={() => openInline({ parent: comment, child: subComment, publishMode: "edit" })}
                                            onDelete={() => deleteComment(subComment)}
                                        >
                                            {isInlineUnder(comment, subComment) ? inlineEditor : null}
                                        </CommentItem>
                                    ))}
                                </ol>
                            ) : null}
                        </CommentItem>
                    ))}
                </ol>
            ) : null}

            <div className="ark-comment-compose">
                <Field label="New comment">
                    <TextArea value={commentText} onChange={setCommentText} rows={3} placeholder="Write a comment" disabled={isBusy && !publishCommentInfo} />
                </Field>
                <div className="ark-comment-compose-actions">
                    <Button
                        type="secondary"
                        size="sm"
                        text="Publish"
                        isBusy={createCommentCall.isLoading && !publishCommentInfo}
                        isDisabled={!commentText.trim()}
                        onClick={handleNewComment}
                    />
                </div>
            </div>
        </section>
    );
}
